/**
 * TIDSSONEVELGEREN — én søkbar combobox over hele IANA-basen, med lesbare navn (#16a).
 *
 * =================================================================================================
 * HVA SOM VAR GALT. Atle, QA-runde 12: «Tidssonen kan ikke endres (låst på Europe/Oslo).» Feltet var
 * en `SuggestField` og kunne i prinsippet ta hva som helst — men det var låst i praksis, av to ting
 * som til sammen gjorde listen usynlig:
 *
 *   1. FORSLAGENE BLE ALDRI SMALERE MENS MAN SKREV. `SuggestField.onOpenChange` fyres bare når
 *      panelet åpner og lukker; kalleren hentet forslagene DER og lot inputens egen `onChange` bare
 *      nullstille en feilmelding. Å skrive «Berl» i et felt som sto på `Europe/Oslo` ga altså den
 *      samme ene raden som før, og listen så ut som om den ikke hadde Berlin.
 *   2. RADENE VAR IDENT-ER. «Europe/Oslo» er databasens nøkkel. Man kunne ikke søke på «Oslo
 *      (UTC+1)» fordi det ikke var noe som sto der, og en som ikke kan IANA-navnene utenat hadde
 *      ingen inngang til listen i det hele tatt.
 *
 * Begge er rettet her, i én komponent, framfor to steder i to skjemaer.
 *
 * =================================================================================================
 * FELTET VISER NAVNET OG LAGRER IDENTEN, og det er hele grunnen til at denne fila finnes framfor at
 * kallerne bruker `SuggestField` direkte. `SuggestField` er fri tekst med en liste ved siden av:
 * den kjenner bare strenger, og strengen den viser er strengen den plukker. Så det som VISES er
 * «Oslo (UTC+1/+2)» og det som SKRIVES til skjemaet er `Europe/Oslo` — oversettelsen mellom de to
 * skjer her, og `resolveTimezone` er veien tilbake for den som skriver uten å åpne panelet.
 *
 * DEN NEKTER INGENTING. En ukjent verdi sendes videre til skjemaet som den er, og kalleren avviser
 * den med `isKnownTimezone` på vei ut av skjemaet — den samme arbeidsdelingen som før. En velger som
 * spiser tastetrykk fordi den ikke kjente igjen det halvferdige ordet, er en velger man slåss med.
 */
import { useMemo, useState, type ReactNode } from 'react';

import { SuggestField } from '@/components/ui/SuggestField';

import {
  resolveTimezone,
  timezoneCity,
  timezoneLabel,
  timezoneOptions,
  type TimezoneOption,
} from './timezones';

export interface TimezoneFieldProps {
  /** The IANA id the form holds. `''` before anything is chosen. */
  value: string;
  /**
   * The id when the text resolves to one, the raw text when it does not.
   *
   * Handing the raw text on rather than swallowing it is deliberate: the caller already refuses
   * an unknown zone on submit and names the field, and a control that silently kept the last
   * good value would let somebody type a zone that does not exist and press Lagre on the one
   * that does.
   */
  onChange: (next: string) => void;
  id?: string;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'data-testid'?: string;
}

/**
 * What to search the catalogue for, given what is in the box.
 *
 * The box holds a LABEL once something is chosen, and «Oslo (UTC+1/+2)» matches no zone id. So a
 * resolvable text is searched by its city, and an unresolvable one has any trailing clock stripped
 * off — which is the state somebody is in when they select a label and start editing it.
 */
function queryFor(text: string): string {
  const zone = resolveTimezone(text);
  if (zone !== null) return timezoneCity(zone);
  return text.replace(/\s*\(UTC[^)]*\)\s*$/i, '').trim();
}

export function TimezoneField({
  value,
  onChange,
  id,
  name,
  disabled = false,
  readOnly = false,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'data-testid': testId,
}: TimezoneFieldProps): ReactNode {
  /*
   * The text is this component's, seeded from the form's value exactly once — the same discipline
   * every form in this product applies to `defaultValues`. The dialogs that hold this field
   * UNMOUNT between openings, so a different festival is a different instance; an effect copying
   * the prop into state on every change would wipe half-typed words the moment a background
   * refetch handed the page a new object with the same zone in it.
   */
  const [text, setText] = useState(() => timezoneLabel(value));
  const [options, setOptions] = useState<readonly TimezoneOption[]>([]);

  const labels = useMemo(() => options.map((option) => option.label), [options]);

  function commit(next: string): void {
    setText(next);
    onChange(resolveTimezone(next) ?? next);
  }

  return (
    <SuggestField
      value={text}
      {...(id === undefined ? {} : { id })}
      {...(name === undefined ? {} : { name })}
      disabled={disabled}
      readOnly={readOnly}
      {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
      {...(invalid === undefined ? {} : { 'aria-invalid': invalid })}
      {...(testId === undefined ? {} : { 'data-testid': testId })}
      items={labels}
      onChange={(event) => {
        const next = event.target.value;
        commit(next);
        // The list narrows on the keystroke, which is the half `SuggestField` cannot do for us:
        // it reports opening and closing, never typing. See the header.
        setOptions(timezoneOptions(queryFor(next)));
      }}
      onOpenChange={(open) => setOptions(open ? timezoneOptions(queryFor(text)) : [])}
      /*
       * ESC SETTER FELTET TILBAKE (QA 20. september).
       *
       * «Skriv Stock, lukk lista med Esc, og Stock blir stående som tekst.» I et fritekstfelt er
       * det riktig — der ER teksten verdien — men en tidssone må finnes i katalogen, og et felt
       * som viser et halvskrevet søkeord ser ut som om det holder et svar. Teksten settes derfor
       * tilbake til den valgte sonen, som er den eneste verdien skjemaet faktisk har.
       */
      onCancel={() => {
        setText(timezoneLabel(value));
        setOptions([]);
      }}
      onPick={(label) => {
        const chosen = options.find((option) => option.label === label);
        setText(chosen === undefined ? label : timezoneLabel(chosen.zone));
        onChange(chosen?.zone ?? resolveTimezone(label) ?? label);
        setOptions([]);
      }}
    />
  );
}
